import { createSelector } from '@ngrx/store';
import {
  TransportOfferResponse,
  TransportOfferStatus,
} from 'src/graphql/generated/graphql.generated';
import { isNullOrWhitespace } from '../../util/string';
import {
  selectTransportOfferEntitiesByRequestId,
  TransportOfferProps,
  TransportOfferRecord,
} from '../generated/transport-offer-record.generated';
import { selectAllTransportOffers } from './entity-store-selectors';

function isOffered(offer: TransportOfferRecord): boolean {
  return (
    offer.status === TransportOfferStatus.Offered &&
    offer.response === TransportOfferResponse.Pending
  );
}

export function selectTransportOfferById(id: string) {
  return createSelector(selectAllTransportOffers, (transportOffers) => {
    return transportOffers.find((t) => t.id === id);
  });
}

export function selectFirstTransportOfferByUnitId(unitId: string, excludedId: string) {
  return createSelector(selectAllTransportOffers, (transportOffers) => {
    return transportOffers.find(
      (t) =>
        t.unitId === unitId && t.id !== excludedId && t.status === TransportOfferStatus.Offered,
    );
  });
}

export function selectTransportOffersByRequestId(requestId: string) {
  return createSelector(selectAllTransportOffers, (offers) =>
    offers.filter((o) => o.requestId === requestId).valueSeq(),
  );
}

export function selectFirstCandidateTransportOfferByRequestId(requestId: string) {
  return createSelector(selectAllTransportOffers, (offers) =>
    offers
      .filter((o) => o.requestId === requestId)
      .sortBy((o) => o.rank)
      .first<TransportOfferRecord>(),
  );
}

export function selectCandidateTransportOffersByRequestId(requestId: string) {
  return createSelector(selectTransportOffersByRequestId(requestId), (transportOffers) => {
    return transportOffers.filter((o) => o.status === TransportOfferStatus.Candidate);
  });
}

export function selectMappedOffersByRequestId(requestId: string) {
  const ignored = [TransportOfferStatus.Cancelled, TransportOfferStatus.Skipped];

  return createSelector(selectAllTransportOffers, (offers) =>
    offers
      .filter((o) => o.requestId === requestId)
      .filter((o) => !ignored.includes(o.status))
      .valueSeq(),
  );
}

export function selectAvailableOrRespondedTransportOffersByRequestId(requestId: string) {
  return createSelector(selectAllTransportOffers, (transportOffers) => {
    return transportOffers
      .filter(
        (t) =>
          t.requestId === requestId &&
          t.status !== TransportOfferStatus.Skipped &&
          t.status !== TransportOfferStatus.Cancelled &&
          (t.status !== TransportOfferStatus.Responded ||
            (t.status === TransportOfferStatus.Responded &&
              (t.response === TransportOfferResponse.Accepted ||
                t.response === TransportOfferResponse.Pending))),
      )
      .sortBy((t) => (isOffered(t) ? 0 : t.rank));
  });
}

export function selectSkippedOrDeclinedTransportOffersByRequestId(requestId: string) {
  return createSelector(selectAllTransportOffers, (transportOffers) => {
    return transportOffers
      .filter(
        (t) =>
          t.requestId === requestId &&
          (t.status === TransportOfferStatus.Skipped ||
            t.status === TransportOfferStatus.Cancelled ||
            t.response === TransportOfferResponse.Declined ||
            t.response === TransportOfferResponse.Aborted),
      )
      .sortBy((t) => t.rank);
  });
}

export function selectCancelledOrDeclinedTransportOffersByRequestId(requestId: string) {
  return createSelector(selectAllTransportOffers, (transportOffers) => {
    return transportOffers
      .filter(
        (t) =>
          t.requestId === requestId &&
          (t.status === TransportOfferStatus.Cancelled ||
            t.response === TransportOfferResponse.Declined ||
            t.response === TransportOfferResponse.Aborted),
      )
      .sortBy((t) => t.rank);
  });
}

export function selectAutoDeclineTransportOffersByRequestId(requestId: string) {
  return createSelector(
    selectAvailableOrRespondedTransportOffersByRequestId(requestId),
    (offers) => {
      return offers.takeWhile(
        (o) => o.declineReasonId != null && o.status !== TransportOfferStatus.Offered,
      );
    },
  );
}

export function selectOfferedOrAcceptedTransportByRequestId(requestId: string) {
  return createSelector(selectAllTransportOffers, (offers) => {
    return offers.find(
      (o) =>
        o.requestId === requestId &&
        (isOffered(o) ||
          (o.response === TransportOfferResponse.Accepted &&
            o.status !== TransportOfferStatus.Cancelled)),
    );
  });
}

export function selectActiveTransportOfferByRequestId(requestId: string) {
  return createSelector(
    selectAvailableOrRespondedTransportOffersByRequestId(requestId),
    selectAutoDeclineTransportOffersByRequestId(requestId),
    (offers, declinableOffers) => {
      const acceptedOffer = offers.find((o) => o.response === TransportOfferResponse.Accepted);
      const offer =
        acceptedOffer != null
          ? acceptedOffer
          : offers.find((o) => declinableOffers.get(o.id) == null);
      return offer == null ? offers.first<TransportOfferRecord | undefined>(undefined) : offer;
    },
  );
}

export function selectOfferedOrCompletedTransportOffersByRequestId(
  requestId: string,
  search: string | null,
) {
  search = search == null || isNullOrWhitespace(search) ? null : search.toLowerCase();
  return createSelector(selectTransportOfferEntitiesByRequestId(requestId), (transportOffers) => {
    return transportOffers
      .filter((t) => {
        if (search == null) {
          return true;
        }
        const searchMatch =
          t.unitDescription != null &&
          (t.unitDescription.toLowerCase().indexOf(search) >= 0 ||
            t.status.toString().toLowerCase().indexOf(search) >= 0 ||
            t.response.toString().toLowerCase().indexOf(search) >= 0);
        const offerStatusMatch = [];
        return (
          t.unitDescription != null &&
          (t.unitDescription.toLowerCase().indexOf(search) >= 0 ||
            t.status.toString().toLowerCase().indexOf(search) >= 0 ||
            t.response.toString().toLowerCase().indexOf(search) >= 0) &&
          (t.status === TransportOfferStatus.Offered ||
            t.status === TransportOfferStatus.Skipped ||
            t.status === TransportOfferStatus.Cancelled ||
            t.response === TransportOfferResponse.Pending ||
            t.response === TransportOfferResponse.Accepted ||
            t.response === TransportOfferResponse.Declined ||
            t.response === TransportOfferResponse.Aborted)
        );
      })
      .sortBy((t) => t.rank);
  });
}

export function selectPreviousOrCurrentReferralOffers(currentOffer: TransportOfferProps) {
  return createSelector(
    selectTransportOfferEntitiesByRequestId(currentOffer.requestId),
    (transportOffers) => {
      return transportOffers.filter(
        (o) =>
          o.status !== TransportOfferStatus.Candidate &&
          (o.isReferral ?? false) &&
          (o.declineReasonId != null || o.id === currentOffer.id),
      );
    },
  );
}
